<i18n src="@/locales/modules/RequestTranslate.json"></i18n>

<template>
	<div class="cbs-repeater-control">
		<legend class="flex justify-between items-center">
			<div class="mr-3">
				{{ fieldData.title }}<span class="text-danger" v-if="isEditable && !fieldData.is_optional">&nbsp;*</span>:
				<i
					v-if="isEditable && isValidRepeaterData"
					:title="$t('AddData')"
					class="nucleo nucleo-c-add-2 btn-add-data-reapeater cursor-pointer text-2xl"
					@click.stop="handleClickAddRepeaterData"
				/>
				<i
					v-if="isEditable && isValidRepeaterData && isRepeaterSupportImportExport"
					:title="$t('ImportData')"
					class="ml-5 md:ml-3 nucleo nucleo-file-upload-93 btn-import-data-reapeater cursor-pointer"
					@click.stop="triggleChooseImportRepeaterDataFile"
				/>
				<i
					v-if="isEditable && isValidRepeaterData && isRepeaterSupportImportExport"
					:title="$t('DownloadImportTemplate')"
					class="ml-5 md:ml-3 nucleo nucleo-file-download-94-2 btn-download-template-reapeater cursor-pointer"
					@click.stop="handleDownloadRepeaterTemplate"
				/>
			</div>
			<cbs-hint
				v-if="
					fieldData.description &&
					fieldData.description.enable &&
					fieldData.description.position === $constant.DYNAMIC_FORM_FIELD_INFO_POSITION.ICON
				"
				:data="fieldData.description && fieldData.description.value"
			/>
		</legend>

		<input
			type="file"
			class="hidden"
			ref="importRepeaterDataRef"
			@change="handleImportRepeaterData"
		/>

		<div
			class="my-1"
			v-if="
				fieldData.description &&
				fieldData.description.enable &&
				fieldData.description.position === $constant.DYNAMIC_FORM_FIELD_INFO_POSITION.TOP
			"
		>{{ fieldData.description && fieldData.description.value }}</div>

		<repeater-content
			v-show="value && value.length"
			:value="fieldData"
			:isEditable="isEditable"
			:mappedAttachObj="mappedAttachObj"
			@input="changeOrderRepeaterData"
			@onRemoveItem="handleRemoveRepeaterItem"
			@onClickRepeaterData="showModalRepeaterData"
		/>

		<div
			class="my-1"
			v-if="
				fieldData.description &&
				fieldData.description.enable &&
				fieldData.description.position === $constant.DYNAMIC_FORM_FIELD_INFO_POSITION.BOTTOM
			"
		>{{ fieldData.description && fieldData.description.value }}</div>

		<div id="import-repeater-loading"></div>

		<el-dialog
			v-if="isDialogActive"
			append-to-body
			class="custom-nested-dialog cbs-repeater-control-dialog"
			:modal="false"
			:visible.sync="isDialogActive"
			:before-close="handleBeforeCloseDialog"
			:title="`${dialogTitle} - ${fieldData.title}`"
		>
			<perfect-scrollbar>
				<cbs-dynamic-form
					v-if="isEditable"
					v-model="localValue"
					class="px-6 repeater"
					:mappedAttachObj="mappedAttachObj"
					@onSetInitData="handleSetInitFormData"
					@onChangeMappedAttachObj="newMappedAttachObj => $emit('onChangeMappedAttachObj', newMappedAttachObj)"
				/>

				<dynamic-form-content
					v-else
					class="p-6 repeater"
					:value="localValue"
					:mappedAttachObj="mappedAttachObj"
				/>
			</perfect-scrollbar>

			<div
				slot="footer"
				v-if="isEditable"
				class="flex items-center sm:px-12 justify-center"
			>
				<vs-button
					class="w-full sm:w-2/5 font-bold"
					@click.stop="handleCreateOrUpdateRepeaterData"
				>{{ $t('Actions.Save') }}</vs-button>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import _ from "lodash";
import XLSX from "xlsx";
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import RepeaterContent from "./RepeaterContent.vue";
import CbsHint from "@/components/cbs-hint/CbsHint.vue";
import DynamicFormContent from "./DynamicFormContent.vue";

export default {
	name: "RepeaterControl",
	provide() {
		return { parentValidator: this.$validator };
	},
	components: {
		CbsHint,
		RepeaterContent,
		DynamicFormContent,
		CbsDynamicForm: () => import ("./CbsDynamicForm")
	},
	props: {
		value: {
			type: Array,
			default() {
				return [];
			}
		},
		fieldData: {
			type: Object,
			required: true
		},
		mappedAttachObj: {
			type: Object,
			default() {
				return {};
			}
		},
		isEditable: {
			type: Boolean,
			default: true
		},
	},
	emits: {
		onChangeMappedAttachObj: {}
	},
	data() {
		return {
			localValue: [],

			isDialogActive: false,
			currentEditIndex: null,
			initFormData: {},
			isFormDirty: false,
			importRepeaterDataRowsLimit: 50,
			currentImportWorkbookIsDate1904: null
		};
	},
	computed: {
		isEditMode() {
			return this.currentEditIndex || this.currentEditIndex === 0;
		},
		dialogTitle() {
			return this.$t(
				this.isEditable
				? this.isEditMode ? 'Actions.Edit' : 'Actions.AddNew'
				: "Actions.ViewDetail"
			);
		},
		repeaterChildrenData() {
			return this.fieldData.children && typeof this.fieldData.children === "string" ? JSON.parse(this.fieldData.children) : this.fieldData.children;
		},
		isValidRepeaterData() {
			return !!this.repeaterChildrenData.length;
		},
		isRepeaterSupportImportExport() {
			return !this.repeaterChildrenData.some(field =>
				field.control === this.$constant.request.control.attach ||
				field.control === this.$constant.request.control.checkbox ||
				field.control === this.$constant.request.control.relation ||
				field.control === this.$constant.request.control.repeater
			);
		},
	},
	watch: {
		localValue: {
			handler: function() {
				this.isFormDirty = JSON.stringify(this.localValue) !== JSON.stringify(this.initFormData);
			},
			deep: true
		}
	},
	methods: {
		formatFormData(form) {
			return form.map(field => {
				if (field.control === this.$constant.request.control.checkbox || field.control === this.$constant.request.control.attach || field.control === this.$constant.request.control.repeater || field.control ===  this.$constant.request.control.relation) {
					field.value = [];
					return field;
				}

				field.value = "";
				return field;
			});
		},
		formatRepeaterData(fieldData) {
			const _cloneRepeaterData = _.cloneDeep(fieldData)
			_cloneRepeaterData.children = _cloneRepeaterData.children && typeof _cloneRepeaterData.children === "string"
				? JSON.parse(_cloneRepeaterData.children)
				: _cloneRepeaterData.children;

			return this.isEditMode ? _cloneRepeaterData.children.map(field => ({
				...field,
				value:
					field.control !== this.$constant.request.control.attach &&
					field.control !== this.$constant.request.control.checkbox &&
					field.control !== this.$constant.request.control.repeater &&
					field.control !== this.$constant.request.control.relation
					? _cloneRepeaterData.value[this.currentEditIndex][field.id] : (
						_cloneRepeaterData.value[this.currentEditIndex][field.id] && !_.isEmpty(_cloneRepeaterData.value[this.currentEditIndex][field.id])
						? _cloneRepeaterData.value[this.currentEditIndex][field.id]
						: []
					)
			})) : this.formatFormData(_cloneRepeaterData.children);
		},
		initValues() {
			this.isFormDirty = false;
		},
		toggleDialog(val = false) {
			this.isDialogActive = val;
		},
		mapImportRepeaterData(repeaterForm, parsedExcelData) {
			const mappedData = [];
			parsedExcelData.some(row => {
				const result = {};
				let isValidData = true;
				repeaterForm.some(field => {
					const mappedFieldTitle = `${!field.is_optional ? field.title + " (*)" : field.title}`;
					if (
						field.is_optional ||
						!field.is_optional &&
						row[mappedFieldTitle] !== null &&
						typeof row[mappedFieldTitle] !== "undefined" &&
						(field.control === this.$constant.request.control.text ||
						field.control === this.$constant.request.control.radio ||
						field.control === this.$constant.request.control.number ||
						field.control === this.$constant.request.control.textarea ||
						field.control === this.$constant.request.control.dropdown ||
						field.control === this.$constant.request.control.datetime ||
						field.control === this.$constant.request.control.calculator)
					) {
						if(row[mappedFieldTitle]) {
							result[field.id] = row[mappedFieldTitle];
							if(field.control === this.$constant.request.control.radio || field.control === this.$constant.request.control.dropdown) {
								(field.options || []).some(option => {
									if(option.content === row[mappedFieldTitle]) {
										result[field.id] = option.id;
										return true;
									}
								})
							}
							if(field.control === this.$constant.request.control.datetime) {
								result[field.id] = XLSX.SSF.format('YYYY-MM-DD HH:mm', row[mappedFieldTitle], { date1904: this.currentImportWorkbookIsDate1904 });
							}
							if(field.control === this.$constant.request.control.text) {
								result[field.id] = row[mappedFieldTitle].replace(/\n/g, " ");
							}
						} else {
							result[field.id] = field.value;
						}
					} else {
						isValidData = false;
						return true;
					}
				});

				if(isValidData) {
					mappedData.push(result);
				}
			});
			return mappedData;
		},
		triggleChooseImportRepeaterDataFile() {
			if(this.$refs.importRepeaterDataRef) {
				this.isAcceptToImportRepeaterData(this.fieldData.title).then(isAccept => {
					if(isAccept) {
						this.$refs.importRepeaterDataRef.click();
					}
				})
			}
		},
		async handleImportRepeaterData(event) {
			if(this.isValidRepeaterDataImportFile(event)) {
				try {
					this.$vs.loading({
						container: "#import-repeater-loading",
						scale: 0.6
					});
					if(this.$refs.importRepeaterDataRef) {
						const file = event.target.files && event.target.files[0];
						const parsedData = await this.parsedExcelData(file);
						const emitValue = this.mapImportRepeaterData(this.repeaterChildrenData, parsedData.results);
						this.$emit("input", emitValue);
						this.$refs.importRepeaterDataRef.value = null;
					}
				} catch (error) {
					this.$helper.notifyError({
						context: this,
						error
					});
				} finally {
					this.$nextTick(() => this.$vs.loading.close("#import-repeater-loading > .con-vs-loading"));
				}
			} else {
				this.$helper.notifyError({
					context: this,
					options: {
						text: this.$t("InvalidRepeaterDataImportFile")
					}
				});
			}
		},
		isValidRepeaterDataImportFile(event) {
			const validExts = [".xlsx", ".xls"];
			let fileExt = event.target.value;
			fileExt = fileExt.substring(fileExt.lastIndexOf('.'));
			return validExts.includes(fileExt);
		},
		parsedExcelData(rawFile) {
			this.$vs.loading();
			return new Promise(resolve => {
				const reader = new FileReader()
				reader.onload = e => {
					const data = e.target.result;
					const workbook = XLSX.read(data, { type: 'array', cellDates: true, sheetRows: this.importRepeaterDataRowsLimit + 1 }); // include header rows
					const firstSheetName = workbook.SheetNames[0];
					const worksheet = workbook.Sheets[firstSheetName];
					const header = this.getHeaderRow(worksheet);
					const results = XLSX.utils.sheet_to_json(worksheet);
					// apply hotfix date
					this.currentImportWorkbookIsDate1904 = workbook.Workbook.WBProps.date1904;
					this.$nextTick(() => this.$vs.loading.close());
					resolve({ header, results });
				}
				reader.readAsArrayBuffer(rawFile);
			});
		},
		getHeaderRow(sheet) {
			const headers = [];
			const range = XLSX.utils.decode_range(sheet['!ref']);
			let C;
			const R = range.s.r;
			/* start in the first row */
			for (C = range.s.c; C <= range.e.c; ++C) { /* walk every column in the range */
				const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })];
				/* find the cell in the first row */
				let hdr = 'UNKNOWN ' + C; // <-- replace with your desired default
				if (cell && cell.t) hdr = XLSX.utils.format_cell(cell);
				headers.push(hdr);
			}
			return headers;
		},
		handleDownloadRepeaterTemplate() {
			this.$vs.loading();
			const limitRows = this.importRepeaterDataRowsLimit;
			const calculationMappedIndex = {};

			// generate headers and set column width
			const repeaterFormFields = this.repeaterChildrenData;
			// create workbook
			const workbook = new ExcelJS.Workbook();
			workbook.creator =  this.$store.state.AppActiveUser && this.$store.state.AppActiveUser.role && this.$store.state.AppActiveUser.role.name || null;

			// create work sheet
			const worksheet = workbook.addWorksheet('Import Template');
			worksheet.columns = repeaterFormFields.map(field => {
				calculationMappedIndex[field.id] = `${field.title}-${field.id}`;

				return {
					width: field.title.length + 5,
					key: `${field.title}-${field.id}`,
					header: `${field.title}${!field.is_optional ? ' (*)': ''}`
				};
			});

			// add rows by limitRows number
			for (let index = 0; index < limitRows; index++) {
				worksheet.addRow([]);
			}

			// add warning row
			worksheet.addRow([`ONLY SUPPORT ${limitRows} ROWS DATA!!!`]);
			worksheet.mergeCells(limitRows + 2, 1, limitRows + 2, repeaterFormFields.length - 1);
			worksheet.getCell(limitRows + 2, 1, limitRows + 2, repeaterFormFields.length - 1).style = {
				alignment :{ vertical: 'middle', horizontal: 'center' },
				font: { bold: true, color: { argb:'ffffff' }},
				fill: {
					type: 'pattern',
					pattern:'solid',
					fgColor:{ argb:'00FF0000' }
				}
			};

			// format cell by field control
			repeaterFormFields.forEach(field => {
				worksheet.getColumn(`${field.title}-${field.id}`).eachCell({ includeEmpty: true }, (cell, rowNumber) => {
					if(rowNumber === 1) {
						cell.style = {font: {bold: true}};
					}

					if(rowNumber > 1 && rowNumber <= limitRows + 1) {
						let validationRules = {
							type: "textLength",
							errorStyle: 'stop',
							showErrorMessage: true,
							errorTitle: this.$t("ImportRepeaterErrors.InvalidValue"),
							error: this.$t("ImportRepeaterErrors.IsRequired"),
							allowBlank: field.is_optional,
							operator: !field.is_optional ? 'greaterThan' : '',
							formulae: !field.is_optional ? [0] : []
						};
						if(field.control === this.$constant.request.control.dropdown || field.control === this.$constant.request.control.radio) {
							validationRules = {
								...validationRules,
								type: 'list',
								error: this.$t("ImportRepeaterErrors.InvalidListType"),
								formulae: field.options ? [`"${field.options.map((option) => option.content).join(",")}"`] : []
							}
						}
						if(field.control === this.$constant.request.control.number || field.control === this.$constant.request.control.calculator) {
							validationRules = {
								...validationRules,
								type: 'decimal',
								error: !validationRules.allowBlank ?
									this.$t("ImportRepeaterErrors.IsRequiredAndMustBeNumber")
									: this.$t("ImportRepeaterErrors.MustBeNumber"),
							}
						}
						if(field.control === this.$constant.request.control.datetime) {
							validationRules = {
								...validationRules,
								type: 'date',
								error: !validationRules.allowBlank ?
									this.$t("ImportRepeaterErrors.IsRequiredAndMustBeValidDateFormat")
									: this.$t("ImportRepeaterErrors.InvalidDateFormat"),
							}
						}

						if (field.control === this.$constant.request.control.calculator) {
							let rexStr = field.cal_exp;
							for (let calNumKey in calculationMappedIndex) {
								if (calculationMappedIndex.hasOwnProperty(calNumKey)) {
									const firstRow = worksheet.getRow(1);
									const fieldLetter = firstRow.getCell(calculationMappedIndex[calNumKey]).address.charAt(0) + rowNumber;
									let repStr = "_" + calNumKey;
									let re = new RegExp(repStr, "g");
									rexStr = rexStr.replace(re, fieldLetter);
								}
							}
							cell.value = {
								formula: rexStr,
								result: field.value
							};
						}

						if(field.control === this.$constant.request.control.text || field.control === this.$constant.request.control.textarea) {
							cell.alignment = { wrapText: true };
						}

						cell.dataValidation = validationRules;
					}
				});
			});

			workbook.xlsx.writeBuffer().then(data => {
				const blob = new Blob([data], { type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
				saveAs(blob, `${this.fieldData.title || "import_template"}.xlsx`);
				this.$nextTick(() => this.$vs.loading.close());
			}).catch(error => {
				this.$nextTick(() => this.$vs.loading.close());
				this.$helper.notifyError({ context: this, error });
			});
		},
		isAcceptToImportRepeaterData(fieldName) {
			return new Promise(resolve => {
				this.$vs.dialog({
					type: "confirm",
					color: "danger",
					title: this.$t("Actions.Confirm"),
					text: this.$t("ImportRepeaterDataConfirm", null, {fieldName}),
					acceptText: this.$t("Actions.Agree"),
					cancelText: this.$t("Actions.Cancel"),
					accept: () => resolve(true),
					cancel: () => resolve(false)
				});
			});
		},
		showModalRepeaterData(fieldData, fieldIndex) {
			this.currentEditIndex = fieldIndex;
			this.localValue = this.formatRepeaterData(fieldData);
			this.initFormData = _.cloneDeep(this.localValue);
			this.toggleDialog(true);
		},
		handleClickAddRepeaterData() {
			this.currentEditIndex = null;
			this.localValue = this.formatRepeaterData(this.fieldData);
			this.initFormData = _.cloneDeep(this.localValue);
			this.toggleDialog(true);
		},
		handleCreateOrUpdateRepeaterData() {
			this.$validator.validateAll().then(result => {
				if (result) {
					const newRepeaterItem = {};
					this.localValue.forEach(field => {
						newRepeaterItem[field.id] = field.value;
						if(field.control === this.$constant.request.control.datetime && field.value) {
							newRepeaterItem[field.id] = moment(field.value).toISOString();
						}

						if (
							!_.isEmpty(field.value) &&
							field.control === this.$constant.request.control.relation &&
							field.value.every(item => typeof item === "object")
						) {
							newRepeaterItem[field.id] = field.value.map(item => item.id);
						}
					});
					if(this.isEditMode) { // Edit repeater item
						this.$emit("input", this.value.map((field, index) => {
							if(index === this.currentEditIndex) {
								return newRepeaterItem;
							}
							return field;
						}));
					} else { // Create repeater item
						this.$emit("input", [...this.value, newRepeaterItem]);
					}
					this.toggleDialog(false);
				}
			});
		},
		handleRemoveRepeaterItem(fieldIndex) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Actions.Confirm"),
				text: this.$t("Notify.DeleteConfirm"),
				acceptText: this.$t("Actions.Agree"),
				cancelText: this.$t("Actions.Cancel"),
				accept: () => {
					this.$emit("input", this.value.filter((field, index) => index !== fieldIndex));
				}
			});
		},
		changeOrderRepeaterData(data) {
			this.$emit("input", data.value);
		},
		handleBeforeCloseDialog(setDialogHide) {
			this.handleCloseDialog(() => {
				this.initValues();
				setDialogHide();
			}, this.isFormDirty);
		},
		handleSetInitFormData(initFormData) {
			this.initFormData = _.cloneDeep(initFormData);
            this.isFormDirty = JSON.stringify(this.localValue) !== JSON.stringify(this.initFormData);
		}
	}
};
</script>

<style lang="scss">
.custom-nested-dialog.cbs-repeater-control-dialog {
	&:not(:last-child) {
		background: none !important;
	}
}

.cbs-repeater-control {
	.btn-add-data-reapeater {
		vertical-align: text-top;
	}

	.btn-import-data-reapeater,
	.btn-download-template-reapeater {
		font-size: 20px;
		vertical-align: middle;
	}

	#import-repeater-loading ::v-deep .con-vs-loading {
		position: relative;
	}
}
</style>
